import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, CameraOff, CheckCircle2, XCircle } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  session: { id: string; class: string | null; topic: string | null; module_code: string | null };
}

/**
 * Restricted scanner used by HOD / Academic Officer to record the CR's own attendance
 * after a session has approved scans. CR cannot scan themselves.
 */
export function AddCrAttendanceDialog({ open, onOpenChange, session }: Props) {
  const { user, hasRole } = useAuth();
  const { toast } = useToast();
  const [scanning, setScanning] = useState(false);
  const [last, setLast] = useState<{ ok: boolean; msg: string } | null>(null);
  const ref = useRef<Html5Qrcode | null>(null);

  const isAcademic = hasRole("head_academic") || hasRole("superadmin");
  const isHod = hasRole("head_administration") || hasRole("superadmin");
  const allowed = isAcademic || isHod;

  useEffect(() => {
    if (!open) stop();
  }, [open]);

  async function start() {
    setLast(null);
    const s = new Html5Qrcode("cr-scanner-region");
    ref.current = s;
    try {
      await s.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 } }, handle, () => {});
      setScanning(true);
    } catch (e: any) {
      toast({ title: "Camera error", description: e?.toString(), variant: "destructive" });
    }
  }

  async function stop() {
    if (ref.current?.isScanning) {
      try { await ref.current.stop(); } catch {}
    }
    setScanning(false);
  }

  async function handle(text: string) {
    if (!text.startsWith("ACA-")) {
      await stop();
      setLast({ ok: false, msg: "Only Academic (ACA) barcodes are accepted" });
      return;
    }

    // Re-verify session is still open
    const { data: sessRow } = await supabase
      .from("attendance_sessions")
      .select("status")
      .eq("id", session.id)
      .maybeSingle();
    if (!sessRow || sessRow.status !== "open") {
      await stop();
      setLast({ ok: false, msg: "Session is closed. CR attendance can no longer be recorded." });
      return;
    }

    // Require at least one approved (present) student record before allowing CR attendance
    const { count: approvedCount } = await supabase
      .from("attendance_records")
      .select("id", { count: "exact", head: true })
      .eq("session_id", session.id)
      .eq("attendance_type", "session")
      .eq("status", "present");
    if (!approvedCount || approvedCount < 1) {
      await stop();
      setLast({ ok: false, msg: "Approve at least one student attendance for this session before recording the CR." });
      return;
    }

    const { data: student } = await supabase
      .from("students")
      .select("id, first_name, last_name, user_id, class")
      .eq("barcode_academic", text)
      .maybeSingle();

    if (!student) { await stop(); setLast({ ok: false, msg: "Student not found" }); return; }

    // Block self-scan
    if (student.user_id && student.user_id === user?.id) {
      await stop();
      setLast({ ok: false, msg: "Self-scan blocked. CRs cannot scan themselves." });
      return;
    }

    // Verify class matches the session's class
    if (session.class && student.class && student.class !== session.class) {
      await stop();
      setLast({ ok: false, msg: `Student belongs to ${student.class}, not ${session.class}` });
      return;
    }

    // Check duplicate
    const { data: existing } = await supabase
      .from("attendance_records")
      .select("id")
      .eq("student_id", student.id)
      .eq("session_id", session.id)
      .eq("attendance_type", "session")
      .maybeSingle();
    if (existing) {
      await stop();
      setLast({ ok: false, msg: "Already scanned for this session" });
      return;
    }

    // HOD scanning → present immediately. Academic Officer scanning CR → pending HOD confirm.
    const status = isHod ? "present" : "pending_hod_confirm";

    const { error } = await supabase.from("attendance_records").insert({
      student_id: student.id,
      session_id: session.id,
      attendance_type: "session",
      scanned_by: user?.id,
      status,
      ...(status === "present" ? { approved_by: user?.id, approved_at: new Date().toISOString() } : {}),
    });

    await stop();
    const name = `${student.first_name} ${student.last_name}`;
    setLast(error
      ? { ok: false, msg: error.message }
      : { ok: true, msg: status === "present" ? `${name} recorded (CR)` : `${name} sent to HOD for confirmation` });
  }

  if (!allowed) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add CR Attendance — {session.topic || "Session"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Scan the CR's Academic (ACA) barcode. {isHod ? "Will be recorded immediately." : "Will be sent to HOD for confirmation."}
          </p>
          <div id="cr-scanner-region" className="w-full rounded border bg-muted/30" style={{ minHeight: scanning ? 280 : 0 }} />
          <div className="flex gap-2">
            {!scanning ? (
              <Button onClick={start} className="flex-1"><Camera className="w-4 h-4 mr-1" />Start Scanner</Button>
            ) : (
              <Button onClick={stop} variant="destructive" className="flex-1"><CameraOff className="w-4 h-4 mr-1" />Stop</Button>
            )}
          </div>
          {last && (
            <div className={`flex items-start gap-2 rounded border p-3 text-sm ${last.ok ? "border-success/50 bg-success/10" : "border-destructive/50 bg-destructive/10"}`}>
              {last.ok ? <CheckCircle2 className="w-4 h-4 text-success mt-0.5" /> : <XCircle className="w-4 h-4 text-destructive mt-0.5" />}
              <span>{last.msg}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
